import {
  AdyenMerchantListItemDto,
  AdyenOnboardingControllerMerchantType,
  AdyenOnboardingRequest,
  AdyenStoreData,
  AdyenUserData,
  CreateEdenredMerchantRequest,
  EdenredConnectMerchantRequest,
  EdenredMerchantListItemDto,
  EdenredOnboardingEmailRequest,
  KlarnaCreateMerchantRequest,
  KlarnaMerchantListItemDto,
  KlarnaOnboardingResendEmailRequest,
  MerchantDto,
  MultiplePaymentMethodsRequest,
  PaymentMethodSettings,
  PaymentMethodSettingsControllerMerchantType,
  PaymentsAPIClient,
  PayoutSettings,
} from '@kotipizzagroup/kotipizza-payments-api-admin-client';
import { getConfiguration } from '../shared/utils/configuration';
import Axios from 'axios';
import { getRequestHeaders } from './utils';
import { MutationFunction, UseQueryResult, useQuery } from '@tanstack/react-query';
import { getPaymentsApiError } from './payments-api.errors';
import { ApiMutationOptions, ApiMutationUiOptions, useApiMutation } from './useApiMutation';
import { isNotFoundError } from '../shared/utils/apiErrors';

let client: PaymentsAPIClient | null = null;

export type AdyenState = {
  [key: string]: {
    payoutSettings: PayoutSettings[];
    paymentMethodSettings: PaymentMethodSettings[];
  };
};

export enum klarnaMerchantStatus {
  PENDING = 0,
  TRANSACTIONS_DISABLED = 1,
  TRANSACTIONS_ENABLED = 2,
  PAYOUTS_ENABLED = 3,
  ADDITIONAL_REVIEW_1 = 4,
  ADDITIONAL_REVIEW_2 = 5,
  SUSPENDED = 6,
  PRE_ASSESSMENT_PENDING = 7,
  DORMANT = 8,
}

export type KlarnaState = {
  currentStatus?: number;
  vatId?: string;
};

type ShopPaymentProviderStatus = {
  shopExternalId: string;
  adyenState: AdyenState;
  klarnaState: KlarnaState;
  updatedAt: string;
};

const getApiClient = (): PaymentsAPIClient => {
  if (client) return client;

  const baseUrl = getConfiguration<string>('paymentsApi.root', '');

  const instance = Axios.create({
    timeout: 20000,
  });

  instance.interceptors.request.use(async (config) => {
    const headers = await getRequestHeaders();
    config.headers = headers;
    return config;
  });

  client = new PaymentsAPIClient(baseUrl, instance);
  return client;
};

export const paymentsApiQueryKeys = {
  allMerchants: 'all-merchants',
  merchant: 'merchant',
  allAdyenMerchants: 'all-adyen-merchants',
  allAdyenUsers: 'all-adyen-users',
  allEdenredMerchants: 'all-edenred-merchants',
  allKlarnaMerchants: 'all-klarna-merchants',
  allShopPaymentStatus: 'all-shop-payment-status',
  methodSettingsEcom: 'method-settings-ecom',
  methodSettingsPos: 'method-settings-pos',
  payoutSettings: 'payout-settings',
};

const getAllMerchants = (): Promise<MerchantDto[]> => {
  const client = getApiClient();
  return client.getAllMerchants();
};

export const useAllMerchants = (): UseQueryResult<MerchantDto[], unknown> => {
  return useQuery<MerchantDto[]>([paymentsApiQueryKeys.allMerchants], getAllMerchants, { retry: false });
};

// A 404 means the shop has no merchant yet, not a failure.
const getMerchant = async (shopExternalId: string): Promise<MerchantDto | null> => {
  const client = getApiClient();
  try {
    return await client.getMerchant(shopExternalId);
  } catch (error) {
    if (isNotFoundError(error)) return null;
    throw error;
  }
};

export const useMerchant = (shopExternalId: string): UseQueryResult<MerchantDto | null, unknown> => {
  return useQuery<MerchantDto | null>(
    [paymentsApiQueryKeys.merchant, shopExternalId],
    () => getMerchant(shopExternalId),
    { retry: false, enabled: !!shopExternalId }
  );
};

export const getAllAdyenMerchants = async (): Promise<AdyenMerchantListItemDto[]> => {
  let currentPage = 1;
  const client = getApiClient();
  const initialResponse = await client.getAllAdyenMerchants(currentPage);
  const merchants = initialResponse.adyenMerchants;
  const promises = [];

  while (initialResponse.pagesTotal >= currentPage) {
    currentPage++;
    promises.push(client.getAllAdyenMerchants(currentPage));
  }

  const responses = await Promise.all(promises);
  responses.forEach((r) => merchants.push(...r.adyenMerchants));
  return merchants;
};

export const useAllAdyenMerchants = (): UseQueryResult<AdyenMerchantListItemDto[], unknown> => {
  return useQuery<AdyenMerchantListItemDto[]>([paymentsApiQueryKeys.allAdyenMerchants], () => getAllAdyenMerchants(), {
    retry: false,
  });
};

export const getAllEdenredMerchants = async (): Promise<EdenredMerchantListItemDto[]> => {
  const client = getApiClient();
  const response = await client.getAllEdenredMerchants();
  const merchants = response.edenredMerchants;
  return merchants;
};

export const useAllEdenredMerchants = (): UseQueryResult<EdenredMerchantListItemDto[], unknown> => {
  return useQuery<EdenredMerchantListItemDto[]>([paymentsApiQueryKeys.allEdenredMerchants], getAllEdenredMerchants, {
    retry: false,
  });
};

export const getAllKlarnaMerchants = async (): Promise<KlarnaMerchantListItemDto[]> => {
  const client = getApiClient();
  const response = await client.getAllKlarnaMerchants();
  const merchants = response.klarnaMerchants;
  return merchants;
};

export const useAllKlarnaMerchants = (): UseQueryResult<KlarnaMerchantListItemDto[], unknown> => {
  return useQuery<KlarnaMerchantListItemDto[]>([paymentsApiQueryKeys.allKlarnaMerchants], getAllKlarnaMerchants, {
    retry: false,
  });
};

export const getAllAdyenUsers = async (): Promise<AdyenUserData[]> => {
  let currentPage = 1;
  const client = getApiClient();
  const initialResponse = await client.getAdyenUsers(currentPage);
  const users = initialResponse.adyenUsers;
  const promises = [];
  while (initialResponse.pagesTotal >= currentPage) {
    currentPage++;
    promises.push(client.getAdyenUsers(currentPage));
  }

  const responses = await Promise.all(promises);
  responses.forEach((r) => users.push(...r.adyenUsers));
  return users;
};

export const useAllAdyenUsers = (): UseQueryResult<AdyenUserData[], unknown> => {
  return useQuery<AdyenUserData[]>([paymentsApiQueryKeys.allAdyenUsers], getAllAdyenUsers, {
    retry: false,
  });
};

const parseKlarnaState = (json: string): KlarnaState => {
  const klarnaState = JSON.parse(json);

  return {
    currentStatus: klarnaState.status?.current,
    vatId: klarnaState.business_information?.vat_id,
  };
};

export const getAllShopPaymentStatus = async (): Promise<ShopPaymentProviderStatus[]> => {
  const client = getApiClient();
  const response = await client.getShopPaymentStatus();

  return response.map((entry) => ({
    shopExternalId: entry.shopExternalId,
    adyenState: JSON.parse(entry.adyenState) as AdyenState,
    klarnaState: parseKlarnaState(entry.klarnaState),
    updatedAt: entry.updatedAt,
  }));
};

export const useAllShopPaymentStatus = (): UseQueryResult<ShopPaymentProviderStatus[], unknown> => {
  return useQuery<ShopPaymentProviderStatus[]>([paymentsApiQueryKeys.allShopPaymentStatus], getAllShopPaymentStatus, {
    retry: false,
  });
};

export const refetchRestaurantPaymentStatus = async (shopExternalId: string) => {
  const client = getApiClient();
  return await client.refetchSingleShopPaymentStatus(shopExternalId);
};

export const connectShopToAdyenMerchant = (
  shopExternalId: string,
  merchantType: AdyenOnboardingControllerMerchantType,
  adyenMerchantId: string
): Promise<void> => {
  const client = getApiClient();
  return client.connectShopToMerchant(shopExternalId, merchantType, { adyenMerchantId });
};

export const connectShopToKlarnaMerchant = (shopExternalId: string, klarnaMerchantId: string): Promise<void> => {
  const client = getApiClient();
  return client.connectKlarnaMerchant({ shopExternalId, merchantId: klarnaMerchantId });
};

export const syncMerchant = (shopExternalId: string) => {
  const client = getApiClient();
  return client.syncMerchant(shopExternalId);
};

export const createAdyenPosStore = (shopExternalId: string, payload: AdyenStoreData) => {
  const client = getApiClient();
  return client.createAdyenPosStore(shopExternalId, payload);
};

export const onboardToAdyen = (shopExternalId: string, payload: AdyenOnboardingRequest) => {
  const client = getApiClient();
  return client.onboardToAdyen(shopExternalId, payload);
};

export const onboardToEdenred = (shopExternalId: string, payload: EdenredConnectMerchantRequest) => {
  const client = getApiClient();
  return client.connectShopToEdenredMerchant(shopExternalId, payload);
};

export const onboardToKlarna = (payload: KlarnaCreateMerchantRequest) => {
  const client = getApiClient();
  return client.createKlarnaMerchant(payload);
};

export const resendKlarnaOnboardingEmail = (payload: KlarnaOnboardingResendEmailRequest) => {
  const client = getApiClient();
  return client.resendKlarnaOnboardingEmail(payload);
};

const getEcomPaymentMethodSettings = (shopExternalId: string): Promise<PaymentMethodSettings[]> => {
  const client = getApiClient();
  return client.getPaymentMethodSettings(shopExternalId, PaymentMethodSettingsControllerMerchantType.ECOM);
};

const getPosPaymentMethodSettings = (shopExternalId: string): Promise<PaymentMethodSettings[]> => {
  const client = getApiClient();
  return client.getPaymentMethodSettings(shopExternalId, PaymentMethodSettingsControllerMerchantType.POS);
};

export const useEcomPaymentMethodSettings = (
  shopExternalId: string,
  enabled = true
): UseQueryResult<PaymentMethodSettings[], unknown> => {
  return useQuery<PaymentMethodSettings[]>(
    [paymentsApiQueryKeys.methodSettingsEcom, shopExternalId],
    () => getEcomPaymentMethodSettings(shopExternalId),
    { retry: false, enabled }
  );
};

export const usePosPaymentMethodSettings = (
  shopExternalId: string,
  enabled = true
): UseQueryResult<PaymentMethodSettings[], unknown> => {
  return useQuery<PaymentMethodSettings[]>(
    [paymentsApiQueryKeys.methodSettingsPos, shopExternalId],
    () => getPosPaymentMethodSettings(shopExternalId),
    { retry: false, enabled }
  );
};

export const requestEcomPaymentMethods = (shopExternalId: string, payload: MultiplePaymentMethodsRequest) => {
  const client = getApiClient();
  return client.requestMultiplePaymentMethods(
    shopExternalId,
    PaymentMethodSettingsControllerMerchantType.ECOM,
    payload
  );
};

export const requestPosPaymentMethods = (shopExternalId: string, payload: MultiplePaymentMethodsRequest) => {
  const client = getApiClient();
  return client.requestMultiplePaymentMethods(shopExternalId, PaymentMethodSettingsControllerMerchantType.POS, payload);
};

export const deactivateFromAdyen = (shopExternalId: string) => {
  const client = getApiClient();
  return client.deactivateFromAdyen(shopExternalId);
};

export const deactivateFromEdenred = (shopExternalId: string) => {
  const client = getApiClient();
  return client.deactivateFromEdenred(shopExternalId);
};

export const deactivateFromKlarna = (shopExternalId: string) => {
  const client = getApiClient();
  return client.disconnectKlarnaMerchant({ shopExternalId });
};

export const activateEPassi = (shopExternalId: string, kotipizzaLegalEntityId: number) => {
  const client = getApiClient();
  return client.activateEPassiForShop(shopExternalId, { kotipizzaLegalEntityId });
};

export const deactivateEPassi = (shopExternalId: string) => {
  const client = getApiClient();
  return client.deactivateEPassiForShop(shopExternalId);
};

export const getAdyenMerchantPayoutSettings = async (merchantId?: string): Promise<PayoutSettings[]> => {
  const client = getApiClient();
  return merchantId ? client.payoutSettings(merchantId) : [];
};

export const useAdyenMerchantPayoutSettings = (
  merchantId?: string,
  enabled = true
): UseQueryResult<PayoutSettings[], unknown> => {
  return useQuery<PayoutSettings[]>(
    [paymentsApiQueryKeys.payoutSettings, merchantId],
    () => getAdyenMerchantPayoutSettings(merchantId),
    { retry: false, enabled }
  );
};

export const activateAdyenMerchant = async (merchantId: string): Promise<void> => {
  const client = getApiClient();
  return client.activateMerchant(merchantId);
};

export const resetKlarnaApiKey = async (klarnaMerchantId: string): Promise<void> => {
  const client = getApiClient();
  return client.resetMerchantApiKey({
    klarnaMerchantId,
  });
};

export const sendEdenredOnboardingEmail = async (request: EdenredOnboardingEmailRequest): Promise<void> => {
  const client = getApiClient();
  return client.sendEdenredOnboardingEmail(request);
};

export const createEdenredMerchant = async (request: CreateEdenredMerchantRequest): Promise<void> => {
  const client = getApiClient();
  return client.createEdenredMerchant(request);
};

const usePaymentsMutation = <TData = unknown, TVariables = void>(
  mutationFn: MutationFunction<TData, TVariables>,
  options: Omit<ApiMutationOptions<TData, TVariables>, 'getError'>
) => useApiMutation(mutationFn, { getError: getPaymentsApiError, ...options });

const merchantListKeys = [
  [paymentsApiQueryKeys.allMerchants],
  [paymentsApiQueryKeys.merchant],
  [paymentsApiQueryKeys.allAdyenMerchants],
];

export const useSyncMerchant = (shopExternalId: string, ui?: ApiMutationUiOptions) =>
  usePaymentsMutation(() => syncMerchant(shopExternalId), { invalidateKeys: merchantListKeys, ...ui });

export const useActivateAdyenMerchant = (merchantId: string, ui?: ApiMutationUiOptions) =>
  usePaymentsMutation(() => activateAdyenMerchant(merchantId), { invalidateKeys: merchantListKeys, ...ui });

export const useConnectAdyenMerchant = (
  shopExternalId: string,
  merchantType: AdyenOnboardingControllerMerchantType,
  ui?: ApiMutationUiOptions<unknown, string>
) =>
  usePaymentsMutation(
    (adyenMerchantId: string) => connectShopToAdyenMerchant(shopExternalId, merchantType, adyenMerchantId),
    { invalidateKeys: merchantListKeys, ...ui }
  );

export const useDeactivateAdyen = (shopExternalId: string, ui?: ApiMutationUiOptions) =>
  usePaymentsMutation(() => deactivateFromAdyen(shopExternalId), {
    invalidateKeys: [
      ...merchantListKeys,
      [paymentsApiQueryKeys.methodSettingsEcom, shopExternalId],
      [paymentsApiQueryKeys.methodSettingsPos, shopExternalId],
      [paymentsApiQueryKeys.payoutSettings],
    ],
    ...ui,
  });

export const useCreateAdyenPosStore = (shopExternalId: string, ui?: ApiMutationUiOptions<unknown, AdyenStoreData>) =>
  usePaymentsMutation((payload: AdyenStoreData) => createAdyenPosStore(shopExternalId, payload), {
    invalidateKeys: [[paymentsApiQueryKeys.allMerchants]],
    ...ui,
  });

export const useRequestEcomPaymentMethods = (
  shopExternalId: string,
  ui?: ApiMutationUiOptions<unknown, MultiplePaymentMethodsRequest>
) =>
  usePaymentsMutation((payload: MultiplePaymentMethodsRequest) => requestEcomPaymentMethods(shopExternalId, payload), {
    invalidateKeys: [[paymentsApiQueryKeys.methodSettingsEcom, shopExternalId]],
    ...ui,
  });

export const useRequestPosPaymentMethods = (
  shopExternalId: string,
  ui?: ApiMutationUiOptions<unknown, MultiplePaymentMethodsRequest>
) =>
  usePaymentsMutation((payload: MultiplePaymentMethodsRequest) => requestPosPaymentMethods(shopExternalId, payload), {
    invalidateKeys: [[paymentsApiQueryKeys.methodSettingsPos, shopExternalId]],
    ...ui,
  });

export const useConnectEdenred = (shopExternalId: string, ui?: ApiMutationUiOptions<unknown, EdenredConnectMerchantRequest>) =>
  usePaymentsMutation((payload: EdenredConnectMerchantRequest) => onboardToEdenred(shopExternalId, payload), {
    invalidateKeys: [[paymentsApiQueryKeys.allMerchants], [paymentsApiQueryKeys.allEdenredMerchants]],
    ...ui,
  });

export const useDeactivateEdenred = (shopExternalId: string, ui?: ApiMutationUiOptions) =>
  usePaymentsMutation(() => deactivateFromEdenred(shopExternalId), {
    invalidateKeys: [[paymentsApiQueryKeys.allMerchants], [paymentsApiQueryKeys.allEdenredMerchants]],
    ...ui,
  });

export const useActivateEPassi = (shopExternalId: string, kotipizzaLegalEntityId: number, ui?: ApiMutationUiOptions) =>
  usePaymentsMutation(() => activateEPassi(shopExternalId, kotipizzaLegalEntityId), {
    invalidateKeys: [[paymentsApiQueryKeys.allMerchants]],
    ...ui,
  });

export const useDeactivateEPassi = (shopExternalId: string, ui?: ApiMutationUiOptions) =>
  usePaymentsMutation(() => deactivateEPassi(shopExternalId), {
    invalidateKeys: [[paymentsApiQueryKeys.allMerchants]],
    ...ui,
  });

export const useConnectKlarna = (shopExternalId: string, ui?: ApiMutationUiOptions<unknown, string>) =>
  usePaymentsMutation((klarnaMerchantId: string) => connectShopToKlarnaMerchant(shopExternalId, klarnaMerchantId), {
    invalidateKeys: [[paymentsApiQueryKeys.allMerchants], [paymentsApiQueryKeys.allKlarnaMerchants]],
    ...ui,
  });

export const useDeactivateKlarna = (shopExternalId: string, ui?: ApiMutationUiOptions) =>
  usePaymentsMutation(() => deactivateFromKlarna(shopExternalId), {
    invalidateKeys: [
      [paymentsApiQueryKeys.allMerchants],
      [paymentsApiQueryKeys.allKlarnaMerchants],
      [paymentsApiQueryKeys.payoutSettings],
    ],
    ...ui,
  });

export const useResetKlarnaApiKey = (klarnaMerchantId: string, ui?: ApiMutationUiOptions) =>
  usePaymentsMutation(() => resetKlarnaApiKey(klarnaMerchantId), ui ?? {});
